import { titleCase } from "@/lib/utils";
import {
  parseDeliverablePlainLanguage,
  type DeliverablePlainLanguageOverride,
} from "./deliverable-eli5";

/**
 * Canonical deliverable type catalog.
 *
 * `deliverables.deliverable_type` is a free-text column on the DB
 * (see migration 216 §12) so the catalog lives here in code. We
 * group the types into three buckets the rest of the app branches
 * on:
 *
 *   • CAPTURE — "field work happened" types. A mission produces
 *     these. Mission grouping in the order wizard defaults to
 *     "one mission per capture-kind deliverable at the same
 *     location", so this list is what the smart-default matcher
 *     consults.
 *
 *   • PRODUCTION — "post-production desk work" types. No mission
 *     by default (the proposal flow can still attach one if the
 *     production shop wants an on-site visit). Marketspace
 *     listings for these are region-less — any vetted production
 *     shop in the network can claim.
 *
 *   • AUTOMATED — pipeline-kind deliverables that run on existing
 *     capture inputs. SpaceSight is the canonical example:
 *     `source_deliverable_ids` must contain at least one CAPTURE
 *     type, validated server-side in `createDeliverable`. These
 *     are auto-claimed by the platform org at create time (never
 *     published to marketspace).
 *
 * ## Adding a new type
 *
 * 1. Append it to the right bucket below.
 * 2. If it's a CAPTURE, consider whether it gets its own detail
 *    table. The convention is "one table per rich provider"
 *    (matterport/navvis/leica/faro/drone) + shared schemas for
 *    simple types (plain `output_url` on the deliverables row).
 * 3. Update the matching per-type panel under
 *    `src/components/virtualspace/deliverables/panels/` when
 *    §3.4 lands those.
 *
 * ## Why `as const` arrays instead of an enum
 *
 * TypeScript enums don't tree-shake well and block the `deliverable_type`
 * string from being passed straight into Supabase filters without a
 * cast. The const arrays give us compile-time literal types + the
 * runtime `Set` for validation without either drawback.
 */

export const CAPTURE_DELIVERABLE_TYPES = [
  "matterport_scan",
  "navvis_scan",
  "leica_scan",
  "faro_scan",
  "drone_scan",
  "ricoh_scan",
  "insta360_scan",
  "zillow3d_scan",
  "iguide_scan",
  "cupix_scan",
  "giraffe360_scan",
  "other_capture",
] as const;
export type CaptureDeliverableType = (typeof CAPTURE_DELIVERABLE_TYPES)[number];

export const PRODUCTION_DELIVERABLE_TYPES = [
  "bim_model",
  "floor_plan",
  "point_cloud",
  "deliverable_bundle",
  "asset_photo_set",
  "video_walkthrough",
  "staging_render",
  // `other_production` retained as the "doesn't fit elsewhere"
  // catch-all bucket. Hidden via platform-default
  // `deliverable_type_settings` (is_active=false) so it never
  // surfaces in the wizard, but kept in code so legacy rows +
  // operator-clarified one-offs still validate.
  "other_production",
] as const;
export type ProductionDeliverableType =
  (typeof PRODUCTION_DELIVERABLE_TYPES)[number];

/**
 * Survey-kind deliverables — structured-questionnaire artefacts
 * that REQUIRE a Spatialist on-site to fill them out. Distinct
 * from CAPTURE (which produces a 3D digital twin / scan) and
 * PRODUCTION (which is desk work derived from a capture).
 *
 * Mission semantics match CAPTURE: every survey-kind deliverable
 * gets a `mission_id` at acceptance fanout, and surveys at the
 * same location as captures share the same mission in 'grouped'
 * grouping mode (one trip, one tech, all field work done in
 * one visit).
 *
 * Why a separate category instead of a CAPTURE alias?
 *   • Customer-facing wording is different ("we'll come fill
 *     out a survey", not "we'll capture a 3D model").
 *   • Pricing model is different (per-survey or per-form, not
 *     per-sqft).
 *   • Marketspace claim semantics may diverge later (surveyors
 *     don't necessarily have 3D-capture hardware).
 *
 * `site_survey` was retired here (2026-08-03): it was a taxonomy
 * entry for "engineering-grade site measurement" that never got
 * execution machinery — no `space_surveys` row, no template
 * cascade, no share token, no handoff review module — and
 * SpaceSurvey is the product that actually ships that work. See
 * migration 900 for the catalog-row archive.
 *
 * Add new survey types here when the platform supports new
 * structured field-data products (e.g. compliance audits,
 * energy assessments, condition reports).
 */
export const SURVEY_DELIVERABLE_TYPES = ["space_survey"] as const;
export type SurveyDeliverableType = (typeof SURVEY_DELIVERABLE_TYPES)[number];

/**
 * Public-overlay deliverable types were retired in migration 322
 * (Phase 3 hosting refactor) — captures now get a
 * `public_share_token` directly + an optional VirtualSpace add-on
 * via `hosting_card_config.spaceview`. The shim below is kept so
 * any back-compat caller (e.g. dormant code paths or legacy
 * tests) compiles + always returns `false`. Safe to delete once
 * no `isOverlayDeliverableType` import remains in the tree.
 */
export type OverlayDeliverableType = never;

export function isOverlayDeliverableType(
  _t: string | null | undefined,
): _t is OverlayDeliverableType {
  return false;
}

/**
 * Automated pipeline deliverables. `createDeliverable` validates
 * that every type in this set has a non-empty `source_deliverable_ids`
 * pointing at at least one CAPTURE-kind deliverable before the row
 * lands — the pipeline needs the capture inputs to run.
 *
 * Add here when we onboard a new automated pipeline (e.g. pano-based
 * floor-plan generators, auto-staging runs, etc.).
 */
export const AUTOMATED_DELIVERABLE_TYPES = [
  "spacesight_run",
  "gaussian_splat_tour",
] as const;
export type AutomatedDeliverableType =
  (typeof AUTOMATED_DELIVERABLE_TYPES)[number];

export type DeliverableType =
  | CaptureDeliverableType
  | ProductionDeliverableType
  | SurveyDeliverableType
  | AutomatedDeliverableType;

export const ALL_DELIVERABLE_TYPES: readonly DeliverableType[] = [
  ...CAPTURE_DELIVERABLE_TYPES,
  ...PRODUCTION_DELIVERABLE_TYPES,
  ...SURVEY_DELIVERABLE_TYPES,
  ...AUTOMATED_DELIVERABLE_TYPES,
];

const CAPTURE_SET: ReadonlySet<string> = new Set(CAPTURE_DELIVERABLE_TYPES);
const PRODUCTION_SET: ReadonlySet<string> = new Set(
  PRODUCTION_DELIVERABLE_TYPES,
);
const SURVEY_SET: ReadonlySet<string> = new Set(SURVEY_DELIVERABLE_TYPES);
const AUTOMATED_SET: ReadonlySet<string> = new Set(AUTOMATED_DELIVERABLE_TYPES);

export function isCaptureDeliverableType(
  t: string | null | undefined,
): t is CaptureDeliverableType {
  return typeof t === "string" && CAPTURE_SET.has(t);
}

/** NavVis / Leica / Faro — shared LiDAR detail panel + raw→COPC pipeline. */
export const LIDAR_CAPTURE_DELIVERABLE_TYPES = [
  "navvis_scan",
  "leica_scan",
  "faro_scan",
] as const;
export type LidarCaptureDeliverableType =
  (typeof LIDAR_CAPTURE_DELIVERABLE_TYPES)[number];

const LIDAR_CAPTURE_SET: ReadonlySet<string> = new Set(
  LIDAR_CAPTURE_DELIVERABLE_TYPES,
);

export function isLidarCaptureDeliverableType(
  t: string | null | undefined,
): t is LidarCaptureDeliverableType {
  return typeof t === "string" && LIDAR_CAPTURE_SET.has(t);
}

/**
 * Captures a CAD & BIM set (Spatial As-Built, `bim_model`) can be drawn
 * from: the on-foot 3D scans that leave a measured mesh or point cloud —
 * Matterport and the LiDAR scanners. Founder rule 2026-09-24: a drone map
 * is never the source, not even for the Roof Plan or Exterior Site Plan
 * sheets; those still need the on-foot scan. The 360 cameras (Ricoh Theta,
 * Insta360, Zillow 3D, iGuide, Cupix, Giraffe 360), Other capture and
 * SpaceSurvey do not count either: none leaves a scan a drafter can measure
 * from, and an unnamed provider is not assumed to. Every CAD & BIM order on
 * record at 2026-09-25 carries one of these four.
 */
export const AS_BUILT_SOURCE_CAPTURE_DELIVERABLE_TYPES = [
  "matterport_scan",
  ...LIDAR_CAPTURE_DELIVERABLE_TYPES,
] as const;

const AS_BUILT_SOURCE_CAPTURE_SET: ReadonlySet<string> = new Set(
  AS_BUILT_SOURCE_CAPTURE_DELIVERABLE_TYPES,
);

export function isAsBuiltSourceCaptureType(
  t: string | null | undefined,
): boolean {
  return typeof t === "string" && AS_BUILT_SOURCE_CAPTURE_SET.has(t);
}

/**
 * Capture types that use the per-scope perimeter / site-plan / roof model
 * (migration 479). `drone_scan` is excluded — drone imagery is a SpaceSurvey
 * companion (photo upload + drone_imagery question), not a scoped exterior
 * capture leg like Matterport.
 */
export function isDroneCompanionDeliverableType(
  t: string | null | undefined,
): boolean {
  return t === "drone_scan";
}

export function usesPerScopeCaptureModel(
  t: string | null | undefined,
): boolean {
  return isCaptureDeliverableType(t) && !isDroneCompanionDeliverableType(t);
}

export function isProductionDeliverableType(
  t: string | null | undefined,
): t is ProductionDeliverableType {
  return typeof t === "string" && PRODUCTION_SET.has(t);
}

/**
 * Customer delivery-ready mail: call the work "files" only when every
 * item is a production conversion (floor plan, BIM, photo set, …).
 * Captures, surveys, automated tours, unknown types, and mixed digests
 * stay "viewable" so a Matterport is never a file.
 */
export type DeliveryReadyKind = "files" | "viewable";

export function classifyDeliveryReadyKind(
  types: ReadonlyArray<string | null | undefined>,
): DeliveryReadyKind {
  const known = types.filter(
    (t): t is string => typeof t === "string" && t.trim().length > 0,
  );
  if (known.length === 0) return "viewable";
  return known.every((t) => isProductionDeliverableType(t))
    ? "files"
    : "viewable";
}

export function isSurveyDeliverableType(
  t: string | null | undefined,
): t is SurveyDeliverableType {
  return typeof t === "string" && SURVEY_SET.has(t);
}

export function isAutomatedDeliverableType(
  t: string | null | undefined,
): t is AutomatedDeliverableType {
  return typeof t === "string" && AUTOMATED_SET.has(t);
}

/** Desk work billed with a capture: As-Built, stills, floor plans, automated tours. */
export function isDeskWorkDeliverableType(
  t: string | null | undefined,
): boolean {
  return isProductionDeliverableType(t) || isAutomatedDeliverableType(t);
}

/**
 * Field-kind = anything that requires a Spatialist on-site at
 * acceptance fanout time. Captures + Surveys; Production +
 * Automated are desk/pipeline work and don't get a mission.
 *
 * The acceptance planner consults this to decide whether a
 * deliverable spawns a mission row at fanout time. Per
 * `.cursorrules` Decision #75 + the survey-as-its-own-category
 * change (2026-05-01), surveys join the same mission as
 * captures at the same location in 'grouped' mode — one trip,
 * one tech, both deliverables produced.
 */
export function isFieldDeliverableType(
  t: string | null | undefined,
): t is CaptureDeliverableType | SurveyDeliverableType {
  return isCaptureDeliverableType(t) || isSurveyDeliverableType(t);
}

/**
 * Deliverables eligible for the handoff workbench AND the editing
 * queue / Deliver & Bill entry points: every CAPTURE type plus
 * `space_survey` (which surfaces its SpaceSurvey review module in the
 * handoff workspace).
 *
 * This is the single source of truth for "does this deliverable get a
 * handoff trigger / show on the editing belt". The capture-only
 * `isCaptureDeliverableType` still drives the capture-centric header /
 * snapshot stepper math so surveys never pollute it.
 */
export function isHandoffEligibleDeliverableType(
  t: string | null | undefined,
): boolean {
  return isCaptureDeliverableType(t) || t === "space_survey";
}

/**
 * Healthy, in-flight automated-pipeline states. A `spacesight_run` /
 * `gaussian_splat_tour` sitting in one of these is the cron pipeline
 * doing its job — it does NOT need a human yet, so it stays OFF the
 * editing belt (see `isEditingQueueEligible`). Once the run lands in a
 * terminal/needs-review state (completed / failed / delivered, or a
 * revision is submitted/approved/rejected), it surfaces.
 */
const HEALTHY_AUTOMATED_RUN_STATUSES: ReadonlySet<string> = new Set([
  "requested",
  "queued",
  "scheduled",
  "pending",
  "assigned",
  "in_progress",
  "processing",
  "running",
]);

/**
 * Single source of truth for "does this deliverable belong on the
 * editing queue / assembly belt" (founder decision #5, 2026-06-26).
 *
 * Rules:
 *   • EVERY catalog type rides the belt — captures, surveys, and
 *     production desk work — EXCEPT a `cancelled` deliverable.
 *     Free-text custom types are treated as production and ride too.
 *   • AUTOMATED pipelines (`spacesight_run`, `gaussian_splat_tour`)
 *     are the one exception: a HEALTHY in-progress cron run is hidden
 *     to keep the belt non-noisy. They only surface when they need a
 *     human — i.e. the run has FAILED or is in a needs-review/terminal
 *     state (completed / delivered, or a revision exists).
 *
 * This REPLACES `isHandoffEligibleDeliverableType` as the queue's
 * type gate. The handoff/workbench reject + the delivery page filter
 * both consult this.
 */
export function isEditingQueueEligible(
  t: string | null | undefined,
  opts?: { status?: string | null; latestRevisionStatus?: string | null },
): boolean {
  if (!t) return false;
  const status = opts?.status ?? null;
  if (status === "cancelled") return false;

  if (isAutomatedDeliverableType(t)) {
    // A submitted/approved/rejected revision means a human is in the
    // loop → always surface, regardless of run status.
    if (opts?.latestRevisionStatus) return true;
    // No status known → surface (fail-open so terminal runs aren't
    // accidentally hidden when the caller can't supply status).
    if (!status) return true;
    return !HEALTHY_AUTOMATED_RUN_STATUSES.has(status);
  }

  return true;
}

export function isKnownDeliverableType(
  t: string | null | undefined,
): t is DeliverableType {
  return (
    isCaptureDeliverableType(t) ||
    isProductionDeliverableType(t) ||
    isSurveyDeliverableType(t) ||
    isAutomatedDeliverableType(t)
  );
}

/**
 * Per-type detail-table routing. `null` means the type has no
 * dedicated detail table and stores its output URL directly on
 * `deliverables.output_url`.
 *
 * Used by:
 *   - `createDeliverable` to decide whether to insert a matching
 *     detail row in the same transaction.
 *   - The polymorphic detail page (§3.4) to know which per-type
 *     panel + loader to mount.
 */
export const DELIVERABLE_DETAIL_TABLE: Record<string, string | null> = {
  // Capture types with per-provider schema
  matterport_scan: "matterport_details",
  navvis_scan: "navvis_details",
  leica_scan: "leica_details",
  faro_scan: "faro_details",
  drone_scan: "drone_details",
  // Production types with rich schema
  bim_model: "bim_details",
  floor_plan: "floor_plan_details",
  asset_photo_set: "asset_photo_set_details",
  // Automated types with per-pipeline schema
  gaussian_splat_tour: "gaussian_splat_tours",
  // Everything else stores output on the parent row directly; the
  // polymorphic page shows a "Simple" panel (single output URL +
  // preview card).
};

export type DeliverableDetailSchema =
  | "simple"
  | "matterport"
  | "lidar"
  | "drone"
  | "bim"
  | "cad"
  | "floor_plan"
  | "space_survey"
  | "spacesight"
  | "splat_tour";

export const DETAIL_SCHEMA_TO_TABLE: Record<DeliverableDetailSchema, string | null> = {
  simple: null,
  matterport: "matterport_details",
  lidar: "navvis_details",
  drone: "drone_details",
  bim: "bim_details",
  cad: "cad_details",
  floor_plan: "floor_plan_details",
  space_survey: "space_surveys",
  spacesight: "scan_equipment_classifications",
  splat_tour: "gaussian_splat_tours",
};

/** Deliverable types that use Matterport-style active-space hosting math. */
const MATTERPORT_ACTIVE_SPACES_HOSTING_TYPES = new Set<string>([
  "matterport_scan",
]);

/**
 * Whether the hosting editor may show the Matterport active-spaces
 * ladder and the `active_spaces` pricing basis (Matterport billing).
 */
export function supportsMatterportActiveSpacesHosting(
  deliverableType: string,
): boolean {
  return MATTERPORT_ACTIVE_SPACES_HOSTING_TYPES.has(deliverableType);
}

export function defaultDetailSchemaForType(
  type: string,
): DeliverableDetailSchema | null {
  if (type === "matterport_scan") return "matterport";
  if (type === "navvis_scan" || type === "leica_scan" || type === "faro_scan") {
    return "lidar";
  }
  if (type === "drone_scan") return "drone";
  if (type === "bim_model") return "bim";
  if (type === "floor_plan") return "floor_plan";
  if (type === "space_survey") return "space_survey";
  if (type === "spacesight_run") return "spacesight";
  if (type === "gaussian_splat_tour") return "splat_tour";
  return null;
}

export function detailTableForSchema(
  schema: DeliverableDetailSchema | null | undefined,
): string | null {
  if (!schema) return null;
  return DETAIL_SCHEMA_TO_TABLE[schema] ?? null;
}

/**
 * Pretty labels for the type dropdown in create/edit forms.
 * Kept next to the catalog so adding a new type is one diff.
 */
export const DELIVERABLE_TYPE_LABELS: Record<DeliverableType, string> = {
  matterport_scan: "Matterport",
  navvis_scan: "NavVis",
  leica_scan: "Leica",
  faro_scan: "Faro",
  drone_scan: "Drone",
  ricoh_scan: "Ricoh Theta",
  insta360_scan: "Insta360",
  zillow3d_scan: "Zillow 3D",
  iguide_scan: "iGuide",
  cupix_scan: "Cupix",
  giraffe360_scan: "Giraffe 360",
  other_capture: "Other capture",
  bim_model: "Spatial As-Built",
  floor_plan: "Space Plan",
  point_cloud: "Point Cloud",
  space_survey: "SpaceSurvey",
  deliverable_bundle: "Bundle",
  asset_photo_set: "Space Stills",
  video_walkthrough: "Video walkthrough",
  staging_render: "Virtual staging",
  other_production: "Other production",
  spacesight_run: "SpaceSight",
  gaussian_splat_tour: "Gaussian Splat Tour",
};

/**
 * Customer-facing labels for the public order tracker. Generally no vendor
 * names — Matterport and NavVis are the deliberate exceptions (founder,
 * 2026-07-13): customers know and ask for those products by name.
 */
export const CUSTOMER_DELIVERABLE_LABELS: Record<DeliverableType, string> = {
  matterport_scan: "Matterport",
  navvis_scan: "NavVis",
  leica_scan: "Point cloud capture",
  faro_scan: "Point cloud capture",
  drone_scan: "Aerial capture",
  ricoh_scan: "360° capture",
  insta360_scan: "360° capture",
  zillow3d_scan: "3D tour",
  iguide_scan: "3D tour",
  cupix_scan: "3D tour",
  giraffe360_scan: "3D tour",
  other_capture: "Site capture",
  bim_model: "As-built model",
  floor_plan: "Floor Plan",
  point_cloud: "Point cloud",
  space_survey: "Space survey",
  deliverable_bundle: "Deliverable package",
  asset_photo_set: "Photos of your space",
  video_walkthrough: "Video walkthrough",
  staging_render: "Virtual staging",
  other_production: "Production deliverable",
  spacesight_run: "Space analysis",
  gaussian_splat_tour: "Immersive tour",
};

export function customerDeliverableLabel(type: string): string {
  return (
    CUSTOMER_DELIVERABLE_LABELS[type as DeliverableType] ??
    "Your deliverable"
  );
}

// ── Unified UI metadata ──────────────────────────────────────────────
//
// Two pickers consume this: the order wizard's Scope step, and the
// `/deliverables` standalone page. Before this catalog they each
// hand-rolled their own arrays — and drifted (the wizard was missing
// 4 capture providers + 4 production types, and SpaceSurvey was
// classified differently in each). This is now the only source of
// truth for what shows up in either picker; the type slugs above
// remain the wire format for the DB.

/**
 * Deliverable category — drives picker grouping, fanout behavior,
 * and pricing-model defaults. Four buckets:
 *
 *   • capture    — 3D spatial capture (Matterport, NavVis, Leica,
 *                  Faro, drone, etc.). Requires a mission.
 *   • survey     — Structured field questionnaire (SpaceSurvey).
 *                  Requires a mission. Distinct from
 *                  capture: produces form data, not a 3D twin.
 *                  Added 2026-05-01 — see header note on
 *                  SURVEY_DELIVERABLE_TYPES.
 *   • production — Desk work derived from a capture (BIM, CAD,
 *                  floor plan, video, etc.). No mission.
 *   • automated  — Pipeline-kind that runs on existing captures
 *                  (SpaceSight). No mission, auto-claimed by
 *                  platform org.
 */
export type DeliverableCategory =
  | "capture"
  | "production"
  | "survey"
  | "automated";

/**
 * Mapping from a CAPTURE deliverable type to the provider_markets
 * slug that owns it (used to pull turnaround / capability metadata
 * onto the wizard's capture cards instead of a separate provider
 * picker). Production / automated types don't carry a provider.
 */
export const CAPTURE_TYPE_TO_PROVIDER_SLUG: Partial<
  Record<CaptureDeliverableType, string>
> = {
  matterport_scan: "matterport",
  navvis_scan: "navvis",
  leica_scan: "leica",
  faro_scan: "faro",
  drone_scan: "dji",
  ricoh_scan: "ricoh_theta",
  insta360_scan: "insta360",
  zillow3d_scan: "zillow3d",
  iguide_scan: "iguide",
  cupix_scan: "cupix",
  giraffe360_scan: "giraffe360",
  other_capture: "other",
};

export type DeliverableTypeMeta = {
  slug: DeliverableType;
  label: string;
  category: DeliverableCategory;
  /** Short, picker-card-sized blurb (~50 chars). */
  shortDesc: string;
  /** Customer-facing one-liner used in the wizard's larger cards. */
  longDesc: string;
  /** Capture-only — the provider_markets slug this type maps to. */
  providerSlug?: string;
};

export type DeliverableCatalogEntry = Omit<DeliverableTypeMeta, "slug"> & {
  slug: string;
  /** True for DB-defined custom deliverables. */
  isCustom?: boolean;
  /** Raw settings flags from deliverable_type_settings. */
  isActive?: boolean;
  displayInPublicOrder?: boolean;
  displayInDashboardOrder?: boolean;
  /**
   * Whether a logged-in customer (customer-only org) sees this type
   * in the dashboard order wizard. Independent of the operator/internal
   * (`displayInDashboardOrder`) and anonymous public
   * (`displayInPublicOrder`) audiences. Fail-closed default (false).
   */
  displayInClientOrder?: boolean;
  /**
   * Marketing /services listing. Independent of public Get Started.
   * Fail-closed default (false).
   */
  displayOnWebsite?: boolean;
  /**
   * Marketing mega-menu / upgrade menu. Independent of public Get Started.
   * Fail-closed default (false). Requires Website-on to appear.
   */
  displayInUpgradeMenu?: boolean;
  isRecommended?: boolean;
  deletedAt?: string | null;
  detailSchema?: DeliverableDetailSchema;
};

export type DeliverableTypeSettingsLike = {
  deliverable_type: string;
  /**
   * Owning org. `null` = platform-default row (the seed every
   * tenant inherits when they haven't created an override).
   * Pre-2026-05-09 the merger ignored this field, so when both
   * a tenant override AND a platform default for the same type
   * came back from the same query the result depended on row
   * order — the LAST occurrence won via `Map.set`. PostgREST
   * doesn't guarantee `.or()` result ordering, so a tenant who
   * deactivated `cupix_scan` could still see the platform's
   * `is_active=true` win silently. The merger now prefers
   * non-null `organization_id` rows, making the resolution
   * deterministic regardless of result order.
   */
  organization_id?: string | null;
  is_active?: boolean | null;
  display_in_public_order?: boolean | null;
  display_in_dashboard_order?: boolean | null;
  display_in_client_order?: boolean | null;
  display_on_website?: boolean | null;
  display_in_upgrade_menu?: boolean | null;
  is_recommended?: boolean | null;
  display_label_override?: string | null;
  display_description_override?: string | null;
  category_override?: DeliverableCategory | null;
  is_custom?: boolean | null;
  custom_category?: DeliverableCategory | null;
  provider_slug?: string | null;
  detail_schema?: DeliverableDetailSchema | null;
  sort_order?: number | null;
  deleted_at?: string | null;
  demo_url?: string | null;
  thumbnail_url?: string | null;
  sample_files?: unknown;
  /** Plain-language "What is this?" overrides (migration 1076). */
  plain_eli5?: string | null;
  plain_you_get?: string[] | null;
  plain_good_for?: string | null;
  plain_needs?: string | null;
};

/** Marketing media attached to a deliverable type card. */
export type DeliverableCatalogMedia = {
  demoUrl: string | null;
  thumbnailUrl: string | null;
  sampleFiles: Array<{ label: string; url: string }>;
  /**
   * Tenant "What is this?" copy (migration 1076). Rides with the card
   * media because both are per-type marketing copy from the same
   * settings row and take the same path into every wizard.
   */
  plainLanguage?: DeliverablePlainLanguageOverride | null;
};

export function parseDeliverableSampleFiles(
  raw: unknown,
): Array<{ label: string; url: string }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const label =
        typeof (entry as { label?: unknown }).label === "string"
          ? (entry as { label: string }).label.trim()
          : "";
      const url =
        typeof (entry as { url?: unknown }).url === "string"
          ? (entry as { url: string }).url.trim()
          : "";
      return label && url ? { label, url } : null;
    })
    .filter((entry): entry is { label: string; url: string } => entry !== null);
}

export function parseDeliverableCatalogMedia(
  row: Pick<
    DeliverableTypeSettingsLike,
    | "demo_url"
    | "thumbnail_url"
    | "sample_files"
    | "plain_eli5"
    | "plain_you_get"
    | "plain_good_for"
    | "plain_needs"
  >,
): DeliverableCatalogMedia | null {
  const sampleFiles = parseDeliverableSampleFiles(row.sample_files);
  const demoUrl = row.demo_url?.trim() || null;
  const thumbnailUrl = row.thumbnail_url?.trim() || null;
  const plainLanguage = parseDeliverablePlainLanguage(row);
  if (!demoUrl && !thumbnailUrl && sampleFiles.length === 0 && !plainLanguage) {
    return null;
  }
  return { demoUrl, thumbnailUrl, sampleFiles, plainLanguage };
}

/** Tenant-specific deliverable_type_settings rows win over platform defaults. */
export function buildDeliverableMediaByType(
  settingsRows: readonly DeliverableTypeSettingsLike[] | null | undefined,
): Record<string, DeliverableCatalogMedia> {
  const rows = settingsRows ?? [];
  const settingsByType = new Map<string, DeliverableTypeSettingsLike>();
  for (const row of rows) {
    if (!row.deliverable_type) continue;
    if (row.organization_id !== null && row.organization_id !== undefined) {
      continue;
    }
    settingsByType.set(row.deliverable_type, row);
  }
  for (const row of rows) {
    if (!row.deliverable_type) continue;
    if (row.organization_id === null || row.organization_id === undefined) {
      continue;
    }
    settingsByType.set(row.deliverable_type, row);
  }

  const mediaByType: Record<string, DeliverableCatalogMedia> = {};
  for (const [type, row] of settingsByType) {
    const media = parseDeliverableCatalogMedia(row);
    if (media) mediaByType[type] = media;
  }
  return mediaByType;
}

/**
 * Canonical type catalog with everything both pickers need. Order
 * inside each category is intentional — the most common picks come
 * first so the wizard's grid stays scannable.
 */
export const DELIVERABLE_TYPE_CATALOG: readonly DeliverableTypeMeta[] = [
  // ── Capture ────────────────────────────────────────────────────
  {
    slug: "matterport_scan",
    label: "Matterport",
    category: "capture",
    shortDesc: "Pro2/Pro3 tour with mesh + dollhouse",
    longDesc:
      "Interactive 3D tour with embedded tags + floor plans. Industry-standard interior + light exterior.",
    providerSlug: "matterport",
  },
  {
    slug: "navvis_scan",
    label: "NavVis",
    category: "capture",
    shortDesc: "VLX/MLX mobile SLAM walk-through",
    longDesc:
      "Survey-grade mobile mapping — IVION webviewer + point clouds. High-throughput for large spaces.",
    providerSlug: "navvis",
  },
  {
    slug: "leica_scan",
    label: "Leica",
    category: "capture",
    shortDesc: "BLK/RTC terrestrial laser scan",
    longDesc:
      "BLK360 + RTC360 captures — precision survey-grade laser scans. Specialist operators.",
    providerSlug: "leica",
  },
  {
    slug: "faro_scan",
    label: "Faro",
    category: "capture",
    shortDesc: "Focus / Premium TLS session",
    longDesc:
      "Focus / FreeStyle2 captures — precision laser + point clouds. Strong AEC / industrial fit.",
    providerSlug: "faro",
  },
  {
    slug: "drone_scan",
    label: "Drone",
    category: "capture",
    shortDesc: "Aerial photos, video, and mapping",
    longDesc:
      "Drone — select photos, video, and/or mapping (orthomosaics). FAA Part 107 operator required.",
    providerSlug: "dji",
  },
  {
    slug: "giraffe360_scan",
    label: "Giraffe 360",
    category: "capture",
    shortDesc: "Giraffe360 360 + floor plan combo",
    longDesc:
      "Automated rotating DSLR tours for listings + lease marketing. Residential / real estate focus.",
    providerSlug: "giraffe360",
  },
  {
    slug: "iguide_scan",
    label: "iGuide",
    category: "capture",
    shortDesc: "iGuide iPad + rangefinder visit",
    longDesc:
      "Planitar-branded — residential 360 capture with floor plans baked into the deliverable.",
    providerSlug: "iguide",
  },
  {
    slug: "cupix_scan",
    label: "Cupix",
    category: "capture",
    shortDesc: "Cupix cloud-stitched tour",
    longDesc:
      "360-photo-driven walkthrough — residential + light commercial. Cloud-stitched tour.",
    providerSlug: "cupix",
  },
  {
    slug: "zillow3d_scan",
    label: "Zillow 3D",
    category: "capture",
    shortDesc: "Zillow 3D Home photo-360 tour",
    longDesc:
      "Listing-optimized 3D Home tour. Integrates with real-estate listing feeds.",
    providerSlug: "zillow3d",
  },
  {
    slug: "ricoh_scan",
    label: "Ricoh Theta",
    category: "capture",
    shortDesc: "Theta Z1/X single-shot 360s",
    longDesc:
      "360° panoramic stills — lightweight deliverable for quick pano decks.",
    providerSlug: "ricoh_theta",
  },
  {
    slug: "insta360_scan",
    label: "Insta360",
    category: "capture",
    shortDesc: "Insta360 rig walkthrough",
    longDesc:
      "Lightweight 360 camera capture — supplemental walkthroughs alongside a primary provider.",
    providerSlug: "insta360",
  },
  {
    slug: "other_capture",
    label: "Other capture",
    category: "capture",
    shortDesc: "Provider not listed above",
    longDesc:
      "Another capture platform not listed — operator picks at acceptance.",
    providerSlug: "other",
  },

  // ── Production ─────────────────────────────────────────────────
  {
    slug: "floor_plan",
    label: "Space Plan",
    category: "production",
    shortDesc: "2D floor plan from capture data",
    longDesc:
      "Accurate schematic floor plans from the capture.",
  },
  {
    slug: "point_cloud",
    label: "Point Cloud",
    category: "production",
    shortDesc: "MatterPak / E57 export from a Matterport scan",
    longDesc:
      "Point cloud files that help architects, engineers, contractors, and other construction professionals complete complex workflows. Requires a Matterport capture.",
  },
  {
    slug: "bim_model",
    label: "Spatial As-Built",
    category: "production",
    shortDesc: "As-built CAD or BIM from capture",
    longDesc:
      "Existing-conditions drawing set or model. 3D BIM ships RVT, SKP, IFC, DWG and PDF; 2D CAD ships DWG and PDF. Discipline scope and drawing add-ons come from your pricing template options.",
  },
  // ── Survey ─────────────────────────────────────────────────────
  // Site-visit deliverables that produce structured form data
  // rather than 3D capture. Each gets a mission at acceptance
  // fanout (same as captures), but the customer-facing wording
  // emphasises the survey nature ("Spatialist visits to fill
  // out a structured survey") instead of the capture nature
  // ("Spatialist captures the space"). Pre-2026-05-01 these were
  // bucketed under Production, which mis-implied desk work and
  // hid the on-site requirement on the wizard's Review step.
  {
    slug: "space_survey",
    label: "SpaceSurvey",
    category: "survey",
    shortDesc: "Guided SpaceSurvey questionnaire",
    longDesc:
      "Template-driven survey filled out by the spatialist on-site — photos, equipment scans, sketches, sign-off.",
  },
  {
    slug: "asset_photo_set",
    label: "Space Stills",
    category: "production",
    shortDesc: "Curated still photography from the tour",
    longDesc: "High-quality still photography curated from the capture.",
  },
  {
    slug: "video_walkthrough",
    label: "Video walkthrough",
    category: "production",
    shortDesc: "Produced video tour / sizzle reel",
    longDesc: "Curated video tour cut from the 3D capture.",
  },
  {
    slug: "staging_render",
    label: "Virtual staging",
    category: "production",
    shortDesc: "Virtual furniture / decor inserts",
    longDesc:
      "Photorealistic furnishing + decor rendered into empty spaces.",
  },
  {
    slug: "deliverable_bundle",
    label: "Bundle",
    category: "production",
    shortDesc: "Packaged set priced as one line",
    longDesc:
      "A packaged set of deliverables priced as one line — useful for fixed-fee scopes.",
  },
  {
    slug: "other_production",
    label: "Other production",
    category: "production",
    shortDesc: "Production artefact not listed",
    longDesc:
      "Another post-production deliverable not listed — operator clarifies on acceptance.",
  },

  // ── Automated ──────────────────────────────────────────────────
  {
    slug: "spacesight_run",
    label: "SpaceSight",
    category: "automated",
    shortDesc: "AI MEP + equipment detection run",
    longDesc:
      "AI object + equipment detection across the captured panos. Runs on existing capture deliverables.",
  },
  {
    slug: "gaussian_splat_tour",
    label: "Gaussian Splat Tour",
    category: "automated",
    shortDesc: "Photoreal splat tour from a capture",
    longDesc:
      "Photoreal Gaussian splat tour generated from an existing capture (Matterport panos today; phone/drone in future). Customer-shareable browser viewer, no app install. Operator-triggered conversion.",
  },
];

const CATALOG_BY_SLUG = new Map<string, DeliverableTypeMeta>(
  DELIVERABLE_TYPE_CATALOG.map((m) => [m.slug, m]),
);

export function getDeliverableTypeMeta(
  slug: string | null | undefined,
): DeliverableTypeMeta | null {
  if (!slug) return null;
  return CATALOG_BY_SLUG.get(slug) ?? null;
}

/**
 * Group the catalog by category for the picker UI. Returned in a
 * stable order — capture first (where the journey starts), then
 * production, then automated. Each group exposes a short helper
 * blurb the wizard renders above the cards.
 */
export const DELIVERABLE_CATEGORIES: ReadonlyArray<{
  id: DeliverableCategory;
  label: string;
  helper: string;
  items: readonly DeliverableTypeMeta[];
}> = [
  {
    id: "capture",
    label: "Spatial Capture",
    helper:
      "Someone shows up on-site to capture the space. The chosen platform is the primary provider — picking multiple captures rolls them into one mission per location by default.",
    items: DELIVERABLE_TYPE_CATALOG.filter((m) => m.category === "capture"),
  },
  {
    id: "survey",
    label: "Spatial Survey",
    helper:
      "Someone shows up on-site to fill out a structured survey. Photos, equipment scans, sketches, sign-off — captured against an operator-defined template. Joins the same mission as captures at the same location by default.",
    items: DELIVERABLE_TYPE_CATALOG.filter((m) => m.category === "survey"),
  },
  {
    id: "production",
    label: "Spatial Conversion",
    helper:
      "Desk work produced from a capture. No on-site visit required — matched with production shops on the marketspace once the capture lands.",
    items: DELIVERABLE_TYPE_CATALOG.filter(
      (m) => m.category === "production",
    ),
  },
  {
    id: "automated",
    label: "Spatial Intelligence",
    helper:
      "Pipelines that run on existing captures. No site visit — auto-claimed by the platform once the source capture is uploaded.",
    items: DELIVERABLE_TYPE_CATALOG.filter(
      (m) => m.category === "automated",
    ),
  },
];

/** Human-facing category name (e.g. `survey` → "Spatial Survey"). */
export function labelForDeliverableCategory(
  category: DeliverableCategory,
): string {
  return (
    DELIVERABLE_CATEGORIES.find((c) => c.id === category)?.label ??
    category
  );
}

/** Wizard / catalog blurb for a deliverable category. */
export function helperForDeliverableCategory(
  category: DeliverableCategory,
): string {
  return (
    DELIVERABLE_CATEGORIES.find((c) => c.id === category)?.helper ??
    ""
  );
}

function indexEffectiveDeliverableTypeSettings(
  rows: readonly DeliverableTypeSettingsLike[],
): Map<string, DeliverableTypeSettingsLike> {
  const settingsByType = new Map<string, DeliverableTypeSettingsLike>();
  for (const row of rows) {
    if (!row.deliverable_type) continue;
    if (row.organization_id !== null && row.organization_id !== undefined) {
      continue;
    }
    settingsByType.set(row.deliverable_type, row);
  }
  for (const row of rows) {
    if (!row.deliverable_type) continue;
    if (row.organization_id === null || row.organization_id === undefined) {
      continue;
    }
    settingsByType.set(row.deliverable_type, row);
  }
  return settingsByType;
}

/** Which order surfaces a deliverable type can be added from. */
export type DeliverableTypeAudienceVisibility = {
  isActive: boolean;
  public: boolean;
  client: boolean;
  dashboard: boolean;
};

/**
 * Resolve per-type order-surface visibility from
 * `deliverable_type_settings` rows (tenant overrides win over
 * platform defaults). Same defaults as `mergeDeliverableCatalog`:
 * public / dashboard fail open, the client surface fails closed.
 */
export function buildDeliverableTypeAudienceVisibility(
  settingsRows: readonly DeliverableTypeSettingsLike[] | null | undefined,
): Map<string, DeliverableTypeAudienceVisibility> {
  const settingsByType = indexEffectiveDeliverableTypeSettings(
    settingsRows ?? [],
  );
  const result = new Map<string, DeliverableTypeAudienceVisibility>();
  for (const [slug, settings] of settingsByType) {
    result.set(slug, {
      isActive: settings.is_active ?? true,
      public: settings.display_in_public_order ?? true,
      client: settings.display_in_client_order ?? false,
      dashboard: settings.display_in_dashboard_order ?? true,
    });
  }
  return result;
}

export type ProductionLabKind = "capture_lab" | "spatial_conversion";

export function productionLabKindForCategory(
  category: DeliverableCategory | null | undefined,
): ProductionLabKind {
  return category === "capture" || category === "survey"
    ? "capture_lab"
    : "spatial_conversion";
}

export function productionLabLabel(kind: ProductionLabKind): string {
  return kind === "capture_lab" ? "Capture" : "Conversion";
}

/**
 * Resolve every catalog type to its Production Lab identity. Tenant category
 * overrides win over platform defaults, and unknown/custom types fail into
 * Spatial Conversion just like the belt's production eligibility fallback.
 */
export function buildProductionLabKindByDeliverableType(
  settingsRows: readonly DeliverableTypeSettingsLike[] | null | undefined,
): Map<string, ProductionLabKind> {
  const rows = settingsRows ?? [];
  const settingsByType = indexEffectiveDeliverableTypeSettings(rows);
  const slugs = new Set<string>([
    ...DELIVERABLE_TYPE_CATALOG.map((entry) => entry.slug),
    ...settingsByType.keys(),
  ]);
  const result = new Map<string, ProductionLabKind>();

  for (const slug of slugs) {
    const meta = getDeliverableTypeMeta(slug);
    const settings = settingsByType.get(slug);
    const category = meta
      ? (settings?.category_override ?? meta.category)
      : (settings?.custom_category ??
        settings?.category_override ??
        "production");
    result.set(slug, productionLabKindForCategory(category));
  }

  return result;
}

export function productionLabKindForDeliverableType(
  deliverableType: string,
): ProductionLabKind {
  return productionLabKindForCategory(
    getDeliverableTypeMeta(deliverableType)?.category ?? "production",
  );
}

export function mergeDeliverableCatalog(
  settingsRows: readonly DeliverableTypeSettingsLike[] | null | undefined,
): DeliverableCatalogEntry[] {
  const rows = settingsRows ?? [];
  // Tenant-specific rows ALWAYS win over platform-default rows
  // (`organization_id IS NULL`). Walk the rows twice: pass 1
  // seeds platform defaults, pass 2 overwrites with any
  // tenant-specific overrides. This gives a deterministic
  // result independent of the input array order — earlier the
  // merger relied on `Map.set` ordering, and PostgREST's
  // `.or()` query doesn't guarantee how `null` vs `tenant`
  // rows come back, so the same tenant could see different
  // resolved active flags between page loads.
  const settingsByType = indexEffectiveDeliverableTypeSettings(rows);

  const entries: DeliverableCatalogEntry[] = DELIVERABLE_TYPE_CATALOG.map((meta) => {
    const settings = settingsByType.get(meta.slug);
    const label = settings?.display_label_override?.trim() || meta.label;
    const desc = settings?.display_description_override?.trim() || meta.longDesc;
    return {
      ...meta,
      label,
      shortDesc: settings?.display_description_override?.trim() || meta.shortDesc,
      longDesc: desc,
      category: settings?.category_override ?? meta.category,
      providerSlug: settings?.provider_slug ?? meta.providerSlug,
      isCustom: false,
      isActive: settings?.is_active ?? true,
      displayInPublicOrder: settings?.display_in_public_order ?? true,
      displayInDashboardOrder: settings?.display_in_dashboard_order ?? true,
      // Fail-closed for the client surface: a type with no explicit
      // client setting is HIDDEN from logged-in customers (operators
      // curate via the global toggle or per-client overrides).
      displayInClientOrder: settings?.display_in_client_order ?? false,
      displayOnWebsite: settings?.display_on_website ?? false,
      displayInUpgradeMenu: settings?.display_in_upgrade_menu ?? false,
      isRecommended: settings?.is_recommended ?? false,
      deletedAt: settings?.deleted_at ?? null,
      detailSchema:
        settings?.detail_schema ?? defaultDetailSchemaForType(meta.slug) ?? "simple",
    };
  });

  const staticSlugs = new Set(DELIVERABLE_TYPE_CATALOG.map((entry) => entry.slug));
  // Custom types: walk the *effective* settings map so tenant overrides
  // win once, instead of emitting a card per raw settings row.
  for (const [slug, row] of settingsByType) {
    if (!row.is_custom || staticSlugs.has(slug as DeliverableType)) {
      continue;
    }
    const label =
      row.display_label_override?.trim() ||
      titleCase(slug.replace(/_/g, " "));
    const desc =
      row.display_description_override?.trim() ||
      "Custom deliverable configured in the catalog.";
    entries.push({
      slug,
      label,
      category: row.custom_category ?? row.category_override ?? "production",
      shortDesc: desc,
      longDesc: desc,
      providerSlug: row.provider_slug ?? undefined,
      isCustom: true,
      isActive: row.is_active ?? true,
      displayInPublicOrder: row.display_in_public_order ?? true,
      displayInDashboardOrder: row.display_in_dashboard_order ?? true,
      displayInClientOrder: row.display_in_client_order ?? false,
      displayOnWebsite: row.display_on_website ?? false,
      displayInUpgradeMenu: row.display_in_upgrade_menu ?? false,
      isRecommended: row.is_recommended ?? false,
      deletedAt: row.deleted_at ?? null,
      detailSchema: row.detail_schema ?? "simple",
    });
  }

  return entries
    .filter((entry) => !entry.deletedAt)
    .sort((a, b) => {
      if (a.category !== b.category) {
        const order: DeliverableCategory[] = [
          "capture",
          "survey",
          "production",
          "automated",
        ];
        return order.indexOf(a.category) - order.indexOf(b.category);
      }
      return a.label.localeCompare(b.label);
    });
}

export function groupDeliverableCatalog(
  entries: readonly DeliverableCatalogEntry[],
): ReadonlyArray<{
  id: DeliverableCategory;
  label: string;
  helper: string;
  items: readonly DeliverableCatalogEntry[];
}> {
  return DELIVERABLE_CATEGORIES.map((category) => ({
    ...category,
    items: entries.filter((entry) => entry.category === category.id),
  })).filter((category) => category.items.length > 0);
}

/** A per-client visibility override (deliverable_type_client_visibility row). */
export type ClientDeliverableVisibilityOverride = {
  deliverable_type: string;
  is_visible: boolean;
};

/**
 * Resolve whether a single deliverable type is visible to a LOGGED-IN
 * client (customer-only org) placing their own order.
 *
 * Precedence (highest first):
 *   1. `is_active = false` → always hidden (hard kill switch).
 *   2. A per-client override row for this type → its `is_visible`.
 *   3. The global `displayInClientOrder` toggle (fail-closed: a type
 *      with no client setting defaults hidden via mergeDeliverableCatalog).
 *
 * Pure + side-effect-free so the wizard loader and the submit-time guard
 * share one definition of "can this client order this type".
 */
export function resolveClientDeliverableVisibility(
  entry: Pick<DeliverableCatalogEntry, "slug" | "isActive" | "displayInClientOrder">,
  override?: { is_visible: boolean } | null,
): boolean {
  if (entry.isActive === false) return false;
  if (override) return override.is_visible;
  return entry.displayInClientOrder === true;
}

/**
 * Build a Map of deliverable_type → override row from a flat list of
 * per-client visibility rows (already scoped to one client). Last write
 * wins if duplicates somehow appear.
 */
export function indexClientVisibilityOverrides(
  rows: readonly ClientDeliverableVisibilityOverride[] | null | undefined,
): Map<string, ClientDeliverableVisibilityOverride> {
  const map = new Map<string, ClientDeliverableVisibilityOverride>();
  for (const row of rows ?? []) {
    if (!row?.deliverable_type) continue;
    map.set(row.deliverable_type, row);
  }
  return map;
}
